import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Eye, 
  Share2, 
  Sparkles,
  Save,
  Loader2,
  MessageSquare,
  PlayCircle,
  HelpCircle,
  ChevronDown,
  X
} from 'lucide-react';
import { getInsightsByEpisode, getInsight, updateInsight, publishInsight } from '../../lib/apiClient';
import QAManager from '../../components/workspace/QAManager';

export default function InsightWorkspacePage() {
  const { episodeSlug: episodeId } = useParams(); // Treating slug as ID based on routing
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const brandSlug = searchParams.get('brandSlug');
  const [insights, setInsights] = useState([]);
  const [activeInsightId, setActiveInsightId] = useState(null);
  const [insightData, setInsightData] = useState(null); // The full loaded insight object
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishingInsightId, setPublishingInsightId] = useState(null); // Track which insight is currently publishing
  const [rightTab, setRightTab] = useState('evidence'); // 'evidence' | 'qa'
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(null); // 'insights' | 'all' | null
  const [bulkToast, setBulkToast] = useState('');
  const [episodeTitle] = useState(location.state?.episodeTitle || '');

  // Load sidebar list
  useEffect(() => {
      async function loadList() {
          try {
              const list = await getInsightsByEpisode(episodeId, projectId ? { projectId } : {});
              setInsights(list || []);
              if (list && list.length > 0) {
                  setActiveInsightId(list[0].id);
              }
          } catch (e) {
              console.error('Failed to load insights list', e);
          } finally {
              setLoading(false);
          }
      }
      loadList();
  }, [episodeId]);

  // Load active insight detail
  useEffect(() => {
      if (!activeInsightId) return;
      async function loadDetail() {
          setInsightData(null); // Clear prev
          try {
              const detail = await getInsight(activeInsightId);
              setInsightData(detail);
          } catch (e) {
              console.error('Failed to load insight detail', e);
          }
      }
      loadDetail();
  }, [activeInsightId]);

  const handleSave = async () => {
      if (!insightData) return;
      setSaving(true);
      try {
          await updateInsight(insightData.id, {
              title: insightData.title,
              hook: insightData.hook,
              body: insightData.body,
              takeaway: insightData.takeaway
          });
          // Refresh list to update titles if changed
          const list = await getInsightsByEpisode(episodeId, projectId ? { projectId } : {});
          setInsights(list || []);
      } catch (e) {
          console.error('Save failed', e);
          alert('Failed to save');
      } finally {
          setSaving(false);
      }
  };

  const handlePublish = async () => {
      if (!insightData || insightData.status === 'published') return;
      // Tie publishing state to this specific insight ID
      setPublishingInsightId(insightData.id);
      try {
          await publishInsight(insightData.id);
          setInsightData({ ...insightData, status: 'published' });
          // Refresh list
          const list = await getInsightsByEpisode(episodeId, projectId ? { projectId } : {});
          setInsights(list || []);
      } catch (e) {
          console.error('Publish failed', e);
          alert('Failed to publish');
      } finally {
          setPublishingInsightId(null);
      }
  };

  // Count unpublished insights
  const unpublishedCount = insights.filter(i => i.status !== 'published').length;

  const handleBulkPublishInsights = async () => {
    setShowBulkConfirm(null);
    setShowBulkMenu(false);
    setBulkPublishing(true);
    
    const toPublish = insights.filter(i => i.status !== 'published');
    let successCount = 0;
    
    try {
      for (const insight of toPublish) {
        try {
          await publishInsight(insight.id);
          successCount++;
        } catch (e) {
          console.error(`Failed to publish insight ${insight.id}:`, e);
        }
      }
      
      // Refresh list
      const list = await getInsightsByEpisode(episodeId, projectId ? { projectId } : {});
      setInsights(list || []);
      
      // Update current insight if it was published
      if (insightData && toPublish.some(i => i.id === insightData.id)) {
        setInsightData({ ...insightData, status: 'published' });
      }
      
      setBulkToast(`Successfully published ${successCount} insight${successCount !== 1 ? 's' : ''}`);
      setTimeout(() => setBulkToast(''), 2500);
    } catch (e) {
      console.error('Bulk publish failed:', e);
      alert('Some insights failed to publish');
    } finally {
      setBulkPublishing(false);
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-background text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="h-screen bg-background text-white flex flex-col font-sans overflow-hidden">
      
      {/* Workspace Header */}
      <header className="h-16 border-b border-white/10 bg-surface flex items-center justify-between px-6 flex-shrink-0 z-20">
        <div className="flex items-center gap-4">
          <Link
            to={brandSlug ? `/app/brand/${brandSlug}/episodes` : "/app/projects"}
            className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-bold text-white text-sm leading-tight">Episode Workspace</h1>
            <div className="text-xs text-slate-500">
              <span className="text-slate-300 block leading-snug">{episodeTitle || `${episodeId.substring(0,8)}...`}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-slate-500 animate-pulse">Saving...</span>}
          <button onClick={handleSave} className="px-3 py-1.5 bg-white/5 border border-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-colors flex items-center gap-2">
            <Save className="w-4 h-4" /> Save
          </button>
          {/* Unified Publish Pill with Embedded Chevron */}
          <div className="relative">
            {(() => {
              const isPublishing = !!insightData && publishingInsightId === insightData.id;
              const isPublished = insightData?.status === 'published';
              const isDisabled = isPublishing || bulkPublishing || isPublished;
              const baseClasses = isPublished
                ? 'bg-green-600 cursor-default'
                : isPublishing || bulkPublishing
                  ? 'bg-primary/70 cursor-wait'
                  : 'bg-primary hover:bg-primary-hover cursor-pointer';

              return (
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handlePublish}
                  className={`px-4 py-1.5 text-white text-sm font-bold rounded-lg shadow-glow-blue transition-colors flex items-center gap-2 ${baseClasses}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isPublished ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : isPublishing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                    <span className="whitespace-nowrap">
                      {isPublished ? 'Published' : isPublishing ? 'Publishing…' : 'Publish'}
                    </span>
                  </div>
                  {/* Embedded Chevron: only toggles menu, does not trigger publish */}
                  <div
                    className={`ml-1 flex items-center justify-center rounded-md px-1.5 py-0.5 ${
                      isPublished
                        ? 'bg-black/10'
                        : 'bg-black/10 group-hover:bg-black/20'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!bulkPublishing && !isPublished) {
                        setShowBulkMenu((open) => !open);
                      }
                    }}
                  >
                    {bulkPublishing ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </button>
              );
            })()}

            {/* Dropdown Menu */}
            {showBulkMenu && !bulkPublishing && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-surface border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                <button
                  onClick={() => setShowBulkConfirm('insights')}
                  disabled={unpublishedCount === 0}
                  className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Publish All Insights</span>
                  {unpublishedCount > 0 && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                      {unpublishedCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Bulk Publish Confirmation Modal */}
        {showBulkConfirm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowBulkConfirm(null)}>
            <div className="bg-surface border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Publish All Insights</h3>
                <button onClick={() => setShowBulkConfirm(null)} className="text-slate-400 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-slate-300 text-sm mb-6">
                This will publish <span className="font-bold text-white">{unpublishedCount} insight{unpublishedCount !== 1 ? 's' : ''}</span> and make them visible on public topic pages.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowBulkConfirm(null)}
                  className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkPublishInsights}
                  className="px-4 py-2 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Publish All
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="flex-1 flex overflow-hidden">
        
        {/* Left Sidebar: Topic Navigator */}
        <aside className="w-64 bg-surface border-r border-white/10 flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-white/5">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Generated Insights ({insights.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {insights.map(item => (
              <button 
                key={item.id}
                onClick={() => setActiveInsightId(item.id)}
                className={`w-full text-left p-3 rounded-lg transition-all group ${
                  activeInsightId === item.id 
                    ? 'bg-primary/10 border border-primary/30' 
                    : 'hover:bg-white/5 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium text-sm line-clamp-2 ${activeInsightId === item.id ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>
                    {item.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 rounded ${
                    item.status === 'published' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {item.status}
                  </span>
                  <span className="text-slate-500">{item.topics?.title || 'Topic'}</span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Main Canvas: Insight Editor */}
        <main className="flex-1 overflow-y-auto bg-background p-8">
          {insightData ? (
              <div className="max-w-3xl mx-auto">
                {/* Insight Meta */}
                <div className="mb-8 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 rounded bg-primary/10 text-primary text-xs font-bold uppercase tracking-wide">
                          {insightData.topics?.title || 'Topic'}
                      </span>
                    </div>
                    <input 
                      type="text" 
                      value={insightData.title}
                      onChange={(e) => setInsightData({...insightData, title: e.target.value})}
                      className="w-full bg-transparent text-3xl font-bold text-white border-none focus:ring-0 p-0 placeholder-slate-600"
                      placeholder="Insight Title..."
                    />
                  </div>
                </div>

                {/* Editor Sections */}
                <div className="space-y-8">
                  <EditorSection 
                      title="Hook / Abstract" 
                      content={insightData.hook} 
                      onChange={(val) => setInsightData({...insightData, hook: val})}
                  />
                  <EditorSection 
                      title="Body / Explanation" 
                      content={insightData.body} 
                      onChange={(val) => setInsightData({...insightData, body: val})}
                      multiline
                  />
                  <EditorSection 
                      title="Takeaway" 
                      content={insightData.takeaway} 
                      onChange={(val) => setInsightData({...insightData, takeaway: val})}
                  />
                </div>
              </div>
          ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                  {insights.length === 0 ? 'No insights generated yet.' : <Loader2 className="w-8 h-8 animate-spin"/>}
              </div>
          )}
        </main>

        {/* Right Sidebar: Evidence & Tools */}
        <aside className="w-80 bg-surface border-l border-white/10 flex flex-col flex-shrink-0">
          
          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button 
              onClick={() => setRightTab('evidence')}
              className={`flex-1 py-3 text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                rightTab === 'evidence' 
                  ? 'text-white border-b-2 border-primary bg-primary/5' 
                  : 'text-slate-500 hover:text-white hover:bg-white/5'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              Evidence
            </button>
            <button 
              onClick={() => setRightTab('qa')}
              className={`flex-1 py-3 text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                rightTab === 'qa' 
                  ? 'text-white border-b-2 border-primary bg-primary/5' 
                  : 'text-slate-500 hover:text-white hover:bg-white/5'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              Q&A
            </button>
          </div>

          {/* Evidence Panel */}
          {rightTab === 'evidence' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            
            {/* Transcript Snippets */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> Source Segments
              </h3>
              <div className="space-y-3">
                {insightData?.metadata?.source_segments?.map((ev, i) => (
                  <div key={i} className="p-3 bg-white/5 border border-white/5 rounded-lg group hover:border-primary/30 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono text-primary bg-primary/10 px-1.5 rounded">
                          {formatTime(ev.start_ms)} - {formatTime(ev.end_ms)}
                      </span>
                      <button className="text-slate-500 hover:text-white transition-colors">
                        <PlayCircle className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-4">
                      "{ev.text_excerpt || 'Audio segment'}"
                    </p>
                  </div>
                ))}
                {(!insightData?.metadata?.source_segments || insightData.metadata.source_segments.length === 0) && (
                    <div className="text-xs text-slate-500 italic">No specific segments linked.</div>
                )}
              </div>
            </div>

          </div>
          )}

          {/* Q&A Panel */}
          {rightTab === 'qa' && projectId && (
            <QAManager
              episodeId={episodeId}
              projectId={projectId}
              insightId={activeInsightId}
            />
          )}
        </aside>

      </div>

      {/* Bulk publish toast */}
      {bulkToast && (
        <div className="pointer-events-none fixed bottom-6 right-6 z-50 rounded-lg border border-primary/40 bg-surface px-4 py-3 text-sm text-white shadow-lg">
          {bulkToast}
        </div>
      )}
    </div>
  );
}

function EditorSection({ title, content, onChange, multiline }) {
  return (
    <section className="group">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider group-hover:text-primary transition-colors">{title}</h3>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-2 transition-opacity">
          <button className="text-xs text-primary hover:underline flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> AI Improve
          </button>
        </div>
      </div>
      {multiline ? (
          <textarea 
            className="w-full bg-surface/30 hover:bg-surface/50 border border-transparent hover:border-white/10 rounded-xl p-4 text-slate-300 leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary transition-all min-h-[300px]"
            value={content || ''}
            onChange={(e) => onChange(e.target.value)}
          />
      ) : (
          <textarea 
            className="w-full bg-surface/30 hover:bg-surface/50 border border-transparent hover:border-white/10 rounded-xl p-4 text-slate-300 leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary transition-all min-h-[100px]"
            value={content || ''}
            onChange={(e) => onChange(e.target.value)}
          />
      )}
    </section>
  );
}

function formatTime(ms) {
    if (!ms) return '00:00';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
}
